// authored by Joshua Beedle
import React from 'react';
import { animated, useSpring } from 'react-spring';
import PropTypes from 'prop-types';
import IcomoonReact from 'icomoon-react';
import styled from '@emotion/styled';

import Wrapper from './Wrapper';
import ButtonText from './ButtonText';
import iconSet from '../../images/teambuildr-selection.json';
import Spinner from '../Spinner';

const Icon = styled('img')`
  height: 20px;
  margin-right: 20px;
`;

const ButtonContent = styled('div')`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

/**
 * @param {String} bgColor custom color for button background
 * @param {String} borderColor custom color for button border
 * @param {Boolean} bottom bool to determine align-self to flex-end or not
 * @param {String} customBorderRadius custom radius for button border
 * @param {String} customIcon custom icon for button not in icon json file
 * @param {Boolean} disabled bool to determine disabling the button
 * @param {String} fontSize custom font size for ButtonText
 * @param {Boolean} fullWidth bool to determine streching the button to full width of container
 * @param {String} href optional URL to render button as an anchor link
 * @param {String} icon name of the icon attached to button
 * @param {String} iconColor color of the icon attached to button
 * @param {String} iconMarginTop custom margin-top for attached icon
 * @param {Boolean} iconOnly bool to determine if button is just displaying the icon
 * @param {Number} iconSize custom icon size for attached icon
 * @param {Boolean} isLoading bool to determine displaying a loading spinner in the button
 * @param {Boolean} large bool to determine if the button height is large (48px)
 * @param {Boolean} lightSpinner bool to determine if the spinner should be light
 * @param {String} name title of the button
 * @param {Boolean} noBorder bool to determine if the button is borderless
 * @param {Boolean} noHover bool to determine if the button should not have a hover effect
 * @param {Function} onClick callback fired when the button is clicked
 * @param {String} rel optional rel attribute for anchor (only applies when href is set)
 * @param {Boolean} rounded bool to determine if the button is rounded
 * @param {Boolean} square bool to determine if the button is squared
 * @param {Object} styledSubtitleProps custom props for subtitle text specifically
 * @param {Object} styledTextProps custom props for ButtonText
 * @param {Object} styledWrapperProps custom props for Wrapper
 * @param {String} subtitle optional subtitle text displayed below the button name
 * @param {String} target optional target attribute for anchor (only applies when href is set)
 * @param {String} txtColor custom color for ButtonText
 * @param {String} type button type attribute (e.g. button, submit)
 * @param {Boolean} upperCase bool to determine if text should be upper case
*/

const Button = ({
  borderColor,
  bottom,
  bgColor,
  customBorderRadius,
  customIcon,
  disabled,
  fontSize,
  fullWidth,
  href,
  icon,
  iconColor,
  iconMarginTop,
  iconOnly,
  iconSize,
  isLoading,
  large,
  lightSpinner,
  name,
  noBorder,
  noHover,
  onClick,
  rel,
  rounded,
  square,
  styledSubtitleProps,
  styledTextProps,
  styledWrapperProps,
  subtitle,
  target,
  txtColor,
  type,
  upperCase,
}) => {
  const spinnerAnimation = useSpring({ opacity: 1 });

  return (
    <Wrapper
      borderColor={borderColor}
      bottom={bottom}
      bgColor={bgColor}
      customBorderRadius={customBorderRadius}
      customIcon={customIcon}
      disabled={disabled}
      fullWidth={fullWidth}
      {...(href ? {
        as: 'a', href, target, rel,
      } : {})}
      icon={icon}
      iconMarginTop={iconMarginTop}
      iconOnly={iconOnly}
      iconSize={iconSize}
      isLoading={isLoading}
      large={large}
      noBorder={noBorder}
      noHover={noHover}
      onClick={onClick}
      rounded={rounded}
      square={square}
      type={type}
      {...styledWrapperProps}
    >
      {customIcon ? (
        <Icon alt='button' className='fluid' src={customIcon} />
      ) : (
        icon && (
        <IcomoonReact
          className='icomoon-svg'
          icon={icon && icon}
          iconSet={iconSet}
          size={iconSize}
          color={iconColor}
        />
        )
      )}
      {isLoading ? (
        <animated.div style={spinnerAnimation}>
          <Spinner large={false} saving={lightSpinner} />
        </animated.div>
      ) : (
        !iconOnly && (
          subtitle ? (
            <ButtonContent>
              <ButtonText
                fontSize={fontSize}
                {...styledTextProps}
                txtColor={txtColor}
              >
                {upperCase ? name.toUpperCase() : name}
              </ButtonText>
              <ButtonText
                {...styledSubtitleProps}
                txtColor={txtColor}
              >
                {subtitle}
              </ButtonText>
            </ButtonContent>
          ) : (
            <ButtonText
              fontSize={fontSize}
              {...styledTextProps}
              txtColor={txtColor}
            >
              {upperCase ? name.toUpperCase() : name}
            </ButtonText>
          )
        )
      )}
    </Wrapper>
  );
};

Button.propTypes = {
  bgColor: PropTypes.string,
  borderColor: PropTypes.string,
  bottom: PropTypes.bool,
  customBorderRadius: PropTypes.string,
  customIcon: PropTypes.string,
  disabled: PropTypes.bool,
  fontSize: PropTypes.string,
  fullWidth: PropTypes.bool,
  href: PropTypes.string,
  icon: PropTypes.string,
  iconColor: PropTypes.string,
  iconMarginTop: PropTypes.string,
  iconOnly: PropTypes.bool,
  iconSize: PropTypes.number,
  isLoading: PropTypes.bool,
  large: PropTypes.bool,
  lightSpinner: PropTypes.bool,
  name: PropTypes.string.isRequired,
  noBorder: PropTypes.bool,
  noHover: PropTypes.bool,
  onClick: PropTypes.func,
  rel: PropTypes.string,
  rounded: PropTypes.bool,
  square: PropTypes.bool,
  styledSubtitleProps: PropTypes.instanceOf(Object),
  styledTextProps: PropTypes.instanceOf(Object),
  styledWrapperProps: PropTypes.instanceOf(Object),
  subtitle: PropTypes.string,
  target: PropTypes.string,
  txtColor: PropTypes.string,
  type: PropTypes.string,
  upperCase: PropTypes.bool,
};

Button.defaultProps = {
  bgColor: null,
  borderColor: '#444444',
  bottom: false,
  customBorderRadius: null,
  customIcon: null,
  disabled: false,
  fontSize: '12px',
  fullWidth: false,
  icon: '',
  iconColor: '#444444',
  iconMarginTop: '0px',
  iconOnly: false,
  iconSize: 15,
  isLoading: false,
  large: false,
  lightSpinner: false,
  noBorder: false,
  noHover: false,
  onClick: () => {},
  href: null,
  target: null,
  rel: null,
  rounded: false,
  square: false,
  styledSubtitleProps: {},
  styledTextProps: {},
  styledWrapperProps: {},
  subtitle: '',
  txtColor: '#444444',
  type: '',
  upperCase: false,
};

export default Button;
